// src/server/SdkMgmt.ts

// SDK 调用服务器接口的代码都在这里

import axios from "axios";
import { encrypUtil } from "../utils/EncrypUtil";
import { SdkApi } from "../services/Api";
import { AppStatus, MobileType } from "../services/Macro";
import { BuyTimesResponse, InitAppResponse, LoginResponse, RegisterResponse } from "../components/sdk/SdkInterface";

const CLS_NAME = `SdkMgmt`;

class SdkMgmt {
    private baseUrl: string;
    private apiPrefix: string;

    constructor() {
        this.baseUrl = process.env.REACT_APP_API_BASE_URL || '';
        this.apiPrefix = '/api/sdk';
    }

    /**
     * 注册一个新的SDK账户
     * @param email - 注册邮箱
     * @param password - 登录密码
     * @returns Promise<RegisterResponse>
     */
    async register(email: string, password: string): Promise<RegisterResponse> {
        const DEBUG = false;
        const TAG = `${CLS_NAME}.register() - `;

        try {
            // 调用后端接口
            const requestData = {
                email: email.trim(),
                password,
            };

            let params = await encrypUtil.encodeRequestRsa(requestData)

            if (DEBUG) {
                console.log(TAG, '发起注册请求:', {
                    ...requestData,
                    password: '******'
                });
            }

            const response = await axios.post(SdkApi.REGISTER_ACCOUNT, params)

            if (DEBUG) {
                console.log(TAG, '响应状态:', response.status, response.statusText);
            }

            if (400 === response.data.code) {
                return {
                    success: false,
                    message: response.data.message
                }
            }
            if (200 === response.data.code) {
                const data = response.data.data;
                return {
                    success: true,
                    data: {
                        appId: data.appId,
                        appKey: data.appKey,
                        appSecret: data.appSecret,
                        registerTime: data.registerTime,
                        remainTimes: data.remainTimes,
                    }
                }
            }
            return {
                success: false,
                message: '未知错误'
            }
        } catch (error: any) {
            if (DEBUG) {
                console.error(TAG, '注册请求异常:', error);
            }

            return {
                success: false,
                message: '网络连接失败，请检查网络设置',
            };
        }
    }

    /**
     * 创建一个新的应用
     * @param type - 移动端类型
     * @param appName - 应用名
     * @param appPackage - 包名
     * @param initialQuota - 初始可用次数
     * @param unlimited - 是否无限制
     * @param expireTime - 过期时间
     * @param description - 描述
     * @returns Promise<InitAppResponse>
     */
    async initApp(
        type: MobileType,
        appName: string,
        appPackage: string,
        initialQuota: number,
        unlimited: boolean,
        expireTime: string,
        description: string
    ): Promise<InitAppResponse> {
        const DEBUG = false;
        const TAG = `${CLS_NAME}.initApp() - `;
        const token = localStorage.getItem('sdk_token');

        try {
            // 调用后端接口
            const requestData = {
                type: type,
                appName: appName.trim(),
                packageName: appPackage,
                initialQuota: initialQuota,
                unlimited: unlimited,
                expireTime: expireTime,
                description: description,
                token: token,
            };

            let params = await encrypUtil.encodeRequestRsa(requestData)

            if (DEBUG) {
                console.log(TAG, '发起应用创建请求:', {
                    ...requestData,
                    password: '******'
                });
            }

            const response = await axios.post(SdkApi.INIT_APP, params)

            if (DEBUG) {
                console.log(TAG, '响应状态:', response.status, response.statusText);
            }

            if (400 === response.data.code) {
                return {
                    success: false,
                    message: response.data.message
                }
            }
            if (200 === response.data.code) {
                const data = response.data.data;
                return {
                    success: true,
                    data: {
                        appId: data.appId,
                        appKey: data.appKey,
                        appSecret: data.appSecret,
                        registerTime: data.registerTime,
                        remainTimes: data.remainTimes,
                    }
                }
            }
            return {
                success: false,
                message: '未知错误'
            }
        } catch (error: any) {
            if (DEBUG) {
                console.error(TAG, '注册请求异常:', error);
            }

            return {
                success: false,
                message: '网络连接失败，请检查网络设置',
            };
        }
    }

    /**
     * 登录SDK账户
     * @param email - 注册邮箱
     * @param password - 登录密码
     * @returns Promise<LoginResponse>
     */
    async login(email: string, password: string): Promise<LoginResponse> {
        const DEBUG = true;
        const TAG = `${CLS_NAME}.login() - `;

        try {
            // 调用后端接口
            const requestData = {
                email: email.trim(),
                password,
            };

            let params = await encrypUtil.encodeRequestRsa(requestData);

            if (DEBUG) {
                console.log(TAG, '发起登录请求:', {
                    ...requestData,
                    password: '******'
                });
            }

            const response = await axios.post(SdkApi.LOGIN_ACCOUNT, params);

            if (DEBUG) {
                console.log(TAG, '响应状态:', response.status, response.statusText);
            }

            if (400 === response.data.code) {
                return {
                    success: false,
                    message: response.data.message
                }
            }
            if (200 === response.data.code) {
                const data = response.data.data;
                DEBUG && console.log(TAG, `data:\n`, data);
                return {
                    success: true,
                    data: {
                        email: data.email,
                        token: data.token,
                        totalApps: data.totalApps,
                        userId: data.userId,
                        apps: data.apps,
                        role: data.role,
                    }
                }
            }
            return {
                success: false,
                message: '未知错误'
            }
        } catch (error: any) {
            if (DEBUG) {
                console.error(TAG, '登录请求异常:', error);
            }

            return {
                success: false,
                message: '网络连接失败，请检查网络设置',
            };
        }
    }

    /**
     * 登录SDK账户
     * @param appId - 应用ID
     * @param addQuota - 增加的次数
     * @returns Promise<LoginResponse>
     */
    async buyQuota(appId: number, priceId: number, addQuota: number): Promise<BuyTimesResponse> {
        const DEBUG = true;
        const TAG = `${CLS_NAME}.buyQuota() - `;
        const token = localStorage.getItem('sdk_token');

        try {
            // 调用后端接口
            const requestData = {
                token,
                appId,
                priceId,
                addQuota,
            };

            let params = await encrypUtil.encodeRequestRsa(requestData);

            if (DEBUG) {
                console.log(TAG, '购买新的次数:', {
                    ...requestData
                });
            }

            const response = await axios.post(SdkApi.BUY_TIMES, params);

            if (DEBUG) {
                console.log(TAG, '响应状态:', response.status, response.statusText);
            }

            if (400 === response.data.code) {
                return {
                    success: false,
                    message: response.data.message
                }
            }
            if (200 === response.data.code) {
                const data = response.data.data;
                DEBUG && console.log(TAG, `data:\n`, data);
                return {
                    success: true,
                    data: {
                        appId: data.appId,
                        appName: data.appName,
                        remainingQuota: data.remainingQuota,
                        totalQuota: data.totalQuota,
                        usedQuota: data.usedQuota,
                        payUrl: data.payUrl,
                    }
                }
            }
            return {
                success: false,
                message: '未知错误'
            }
        } catch (error: any) {
            if (DEBUG) {
                console.error(TAG, '登录请求异常:', error);
            }

            return {
                success: false,
                message: '网络连接失败，请检查网络设置',
            };
        }
    }

    /**
     * 获取账号下的应用列表
     * @returns 
     */
    async getAppList() {
        const DEBUG = true;
        const TAG = `${CLS_NAME}.getAppList() - `;
        const token = localStorage.getItem('sdk_token');

        try {
            // 调用后端接口
            const requestData = {
                token: token
            };

            let params = await encrypUtil.encodeRequestRsa(requestData);

            if (DEBUG) {
                console.log(TAG, '获取当前账号下的所有应用列表:', {
                    ...requestData
                });
            }

            const response = await axios.post(SdkApi.GET_APP_LIST, params);

            if (DEBUG) {
                console.log(TAG, '响应状态:', response.status, response.statusText);
            }

            if (400 === response.data.code) {
                return {
                    success: false,
                    message: response.data.message
                }
            }
            if (200 === response.data.code) {
                const data = response.data.data;
                DEBUG && console.log(TAG, `data:\n`, data);
                return {
                    success: true,
                    data: {
                        list: data.apps,
                        totalApps: data.total,
                    }
                }
            }
            return {
                success: false,
                message: '未知错误'
            }
        } catch (error: any) {
            if (DEBUG) {
                console.error(TAG, '登录请求异常:', error);
            }

            return {
                success: false,
                message: '网络连接失败，请检查网络设置',
            };
        }
    }

    /**
     * 获取指定应用的详情
     * @param appId 
     * @returns 
     */
    async getAppDetail(appId: string) {
        const DEBUG = true;
        const TAG = `${CLS_NAME}.getAppDetail() - `;
        const token = localStorage.getItem('sdk_token');

        try {
            // 调用后端接口
            const requestData = {
                token: token
            };

            let params = await encrypUtil.encodeRequestRsa(requestData);

            if (DEBUG) {
                console.log(TAG, '获取当前账号下的所有应用列表:', {
                    ...requestData
                });
            }

            const response = await axios.post(SdkApi.GET_APP_DETAIL, params);

            if (DEBUG) {
                console.log(TAG, '响应状态:', response.status, response.statusText);
            }

            if (400 === response.data.code) {
                return {
                    success: false,
                    message: response.data.message
                }
            }
            if (200 === response.data.code) {
                const data = response.data.data;
                DEBUG && console.log(TAG, `data:\n`, data);
                return {
                    success: true,
                    data: {
                        appId: data.appId,
                        appKey: data.appKey,
                        appSecret: data.appSecret,
                    }
                }
            }
            return {
                success: false,
                message: '未知错误'
            }
        } catch (error: any) {
            if (DEBUG) {
                console.error(TAG, '登录请求异常:', error);
            }

            return {
                success: false,
                message: '网络连接失败，请检查网络设置',
            };
        }
    }

    /**
     * 获取SDK调用列表
     * @returns 
     */
    async getLogList(
        type: number = 0,
        userId?: number,
        appId?: number,
        appName?: string,
        page: number = 1,
        pageSize: number = 10,
        startTime?: string,
        endTime?: string
    ) {
        const DEBUG = true;
        const TAG = `${CLS_NAME}.getLogList() - `;
        const token = localStorage.getItem('sdk_token');

        try {
            // 调用后端接口
            const requestData = {
                type: type,
                userId: userId,
                appId: appId,
                appName: appName,
                page: page,
                pageSize: pageSize,
                startTime: startTime,
                endTime: endTime,
                token: token
            };

            let params = await encrypUtil.encodeRequestRsa(requestData);

            if (DEBUG) {
                console.log(TAG, '获取SDK调用列表:', {
                    ...requestData
                });
            }

            const response = await axios.post(SdkApi.GET_LOG_LIST, params);

            if (DEBUG) {
                console.log(TAG, '响应状态:', response.status, response.statusText);
            }

            if (400 === response.data.code) {
                return {
                    success: false,
                    message: response.data.message
                }
            }
            if (200 === response.data.code) {
                const data = response.data.data;
                DEBUG && console.log(TAG, `data:\n`, data);
                return {
                    success: true,
                    data: data
                }
            }
            return {
                success: false,
                message: '未知错误'
            }
        } catch (error: any) {
            if (DEBUG) {
                console.error(TAG, '登录请求异常:', error);
            }

            return {
                success: false,
                message: '网络连接失败，请检查网络设置',
            };
        }
    }

    /**
    * 激活应用
    * @param appId - 应用ID
    * @returns Promise<any>
    */
    async activateApp(appId: number): Promise<any> {
        const DEBUG = true;
        const TAG = `${CLS_NAME}.activateApp() - `;
        const token = localStorage.getItem('sdk_token');

        try {
            const requestData = {
                token: token,
                appId: appId,
                status: AppStatus.ACTIVE
            };

            let params = await encrypUtil.encodeRequestRsa(requestData);

            if (DEBUG) {
                console.log(TAG, '激活应用:', requestData);
            }

            const response = await axios.post(SdkApi.CHANGE_APP_STATUS, params);

            if (DEBUG) {
                console.log(TAG, '响应状态:', response.status, response.statusText);
            }

            if (400 === response.data.code) {
                return {
                    success: false,
                    message: response.data.message
                }
            }
            if (200 === response.data.code) {
                return {
                    success: true,
                    data: response.data.data
                }
            }
            return {
                success: false,
                message: '未知错误'
            }
        } catch (error: any) {
            if (DEBUG) {
                console.error(TAG, '激活应用异常:', error);
            }
            return {
                success: false,
                message: '网络连接失败，请检查网络设置',
            };
        }
    }

    /**
     * 停用应用
     * @param appId - 应用ID
     * @returns Promise<any>
     */
    async inactivateApp(appId: number): Promise<any> {
        const DEBUG = true;
        const TAG = `${CLS_NAME}.inactivateApp() - `;
        const token = localStorage.getItem('sdk_token');

        try {
            const requestData = {
                token: token,
                appId: appId,
                status: AppStatus.INACTIVE
            };

            let params = await encrypUtil.encodeRequestRsa(requestData);

            if (DEBUG) {
                console.log(TAG, '停用应用:', requestData);
            }

            const response = await axios.post(SdkApi.CHANGE_APP_STATUS, params);

            if (DEBUG) {
                console.log(TAG, '响应状态:', response.status, response.statusText);
            }

            if (400 === response.data.code) {
                return {
                    success: false,
                    message: response.data.message
                }
            }
            if (200 === response.data.code) {
                return {
                    success: true,
                    data: response.data.data
                }
            }
            return {
                success: false,
                message: '未知错误'
            }
        } catch (error: any) {
            if (DEBUG) {
                console.error(TAG, '停用应用异常:', error);
            }
            return {
                success: false,
                message: '网络连接失败，请检查网络设置',
            };
        }
    }

    /**
     * 删除应用
     * @param appId - 应用ID
     * @returns Promise<any>
     */
    async deleteApp(appId: number): Promise<any> {
        const DEBUG = true;
        const TAG = `${CLS_NAME}.deleteApp() - `;
        const token = localStorage.getItem('sdk_token');

        try {
            const requestData = {
                token: token,
                appId: appId,
            };

            let params = await encrypUtil.encodeRequestRsa(requestData);

            if (DEBUG) {
                console.log(TAG, '删除应用:', requestData);
            }

            const response = await axios.post(SdkApi.DEL_APP, params);

            if (DEBUG) {
                console.log(TAG, '响应状态:', response.status, response.statusText);
            }

            if (400 === response.data.code) {
                return {
                    success: false,
                    message: response.data.message
                }
            }
            if (200 === response.data.code) {
                return {
                    success: true,
                    data: response.data.data
                }
            }
            return {
                success: false,
                message: '未知错误'
            }
        } catch (error: any) {
            if (DEBUG) {
                console.error(TAG, '删除应用异常:', error);
            }
            return {
                success: false,
                message: '网络连接失败，请检查网络设置',
            };
        }
    }

    /**
     * 上传SDK使用文档
     */
    async uploadDoc(params: any) {
        const DEBUG = true;
        const TAG = `${CLS_NAME}.uploadDoc() - `;

        let token = localStorage.getItem('sdk_token') || null;
        let lang = localStorage.getItem('lang') || 'zh_CN';

        if (token) {

            const formData = new FormData();
            formData.append('token', token);
            formData.append('lang', lang.split('_')[0]);
            formData.append('title', params.title);
            formData.append('category', params.category);
            formData.append('description', params.description);
            formData.append('file', params.file);
            // 注意: 要打印 formData 需要先转型
            DEBUG && console.log(TAG, `formData:\n`, Array.from(formData.entries()));

            const response = await axios.post(
                SdkApi.UPLOAD_DOC, formData,
                { headers: { "Content-Type": "multipart/form-data", }, }
            );
            DEBUG && console.log(TAG, `response:\n`, response);
            return {
                success: true,
                message: 'TODO',
                data: response.data
            };
        }
        return {
            success: false,
            message: 'No token'
        }
    }

    /**
     * 上传SDK文件
     */
    async uploadSdk(params: any) {
        const DEBUG = true;
        const TAG = `${CLS_NAME}.uploadSdk() - `;

        let token = localStorage.getItem('sdk_token') || null;
        let lang = localStorage.getItem('lang') || 'zh_CN';

        if (token) {

            const formData = new FormData();
            formData.append('token', token);
            formData.append('lang', lang.split('_')[0]);
            formData.append('file', params.file);
            formData.append('platform', params.platform);
            formData.append('version', params.version);
            formData.append('description', params.description);
            // 注意: 要打印 formData 需要先转型
            DEBUG && console.log(TAG, `formData:\n`, Array.from(formData.entries()));

            const response = await axios.post(
                SdkApi.UPLOAD_SDK, formData,
                { headers: { "Content-Type": "multipart/form-data", }, }
            );
            DEBUG && console.log(TAG, `response:\n`, response);
            return {
                success: true,
                message: 'TODO',
                data: response.data
            };
        }
        return {
            success: false,
            message: 'No token'
        }
    }

    async getDownloadInfo() {
        const DEBUG = true;
        const TAG = `${CLS_NAME}.getDownloadInfo() - `;

        // 获取下载链接信息不需要token
        let lang = localStorage.getItem('lang') || 'zh_CN';
        const requestData = {
            lang: lang,
        };

        let params = await encrypUtil.encodeRequestRsa(requestData);

        const response = await axios.post(SdkApi.GET_DOWNLOAD_INFO, params);
        DEBUG && console.log(TAG, `response:\n`, response);
        return {
            success: true,
            message: 'TODO',
            data: response.data.data
        };
    }

    // 在 sdkMgmt 中添加方法（示例）
    async updateApp(params: { appId: number; quotaDelta: number; endTime: string, unlimited: number }) {
        const DEBUG = true;
        const TAG = `${CLS_NAME}.updateApp() - `;

        let token = localStorage.getItem('sdk_token') || null;
        let lang = localStorage.getItem('lang') || 'zh_CN';

        if (token) {
            const requestData = {
                ...params,
                token: token,
                lang: lang,
            };

            let encodeParams = await encrypUtil.encodeRequestRsa(requestData);

            const response = await axios.post(SdkApi.UPDATE_APP, encodeParams);
            DEBUG && console.log(TAG, `response:\n`, response);
            return {
                success: true,
                message: 'TODO',
                data: response.data.data
            };
        }
        return {
            success: false,
            message: 'No token'
        }
    }

    async getClientLogs(page: number, pageSize: number) {
        const DEBUG = true;
        const TAG = `${CLS_NAME}.getLogList() - `;
        const token = localStorage.getItem('sdk_token');

        try {
            // 调用后端接口
            const requestData = {
                page: page,
                size: pageSize,
                token: token
            };

            let params = await encrypUtil.encodeRequestRsa(requestData);

            if (DEBUG) {
                console.log(TAG, '获取客户端调试日志列表:', {
                    ...requestData
                });
            }

            const response = await axios.post(SdkApi.GET_CLIENT_LOGS, params);

            if (DEBUG) {
                console.log(TAG, '响应状态:', response.status, response.statusText);
            }

            if (400 === response.data.code) {
                return {
                    success: false,
                    message: response.data.message
                }
            }
            if (200 === response.data.code) {
                const data = response.data.data;
                DEBUG && console.log(TAG, `data:\n`, data);
                return {
                    success: true,
                    data: data
                }
            }
            return {
                success: false,
                message: '未知错误'
            }
        } catch (error: any) {
            if (DEBUG) {
                console.error(TAG, '登录请求异常:', error);
            }

            return {
                success: false,
                message: '网络连接失败，请检查网络设置',
            };
        }
    }

    /** 删除过期使用记录(指定记录时间)
     * @param date 指定过期时间，此时间之前的使用记录将会被清空
     * @returns 
     */
    async delUseRecord(date: string) {
        const DEBUG = true;
        const TAG = `${CLS_NAME}.delUseRecord() - `;
        const token = localStorage.getItem('sdk_token');

        try {
            // 调用后端接口
            const requestData = {
                date: date,
                token: token
            };

            let params = await encrypUtil.encodeRequestRsa(requestData);

            if (DEBUG) {
                console.log(TAG, '删除过期使用记录:', {
                    ...requestData
                });
            }

            const response = await axios.post(SdkApi.DEL_SDK_USAGE_RECORD_BEFORE, params);

            if (DEBUG) {
                console.log(TAG, '响应状态:', response.status, response.statusText);
            }

            if (400 === response.data.code) {
                return {
                    success: false,
                    message: response.data.message
                }
            }
            if (200 === response.data.code) {
                const data = response.data.data;
                DEBUG && console.log(TAG, `data:\n`, data);
                return {
                    success: true,
                    data: data
                }
            }
            return {
                success: false,
                message: '未知错误'
            }
        } catch (error: any) {
            if (DEBUG) {
                console.error(TAG, '登录请求异常:', error);
            }

            return {
                success: false,
                message: '网络连接失败，请检查网络设置', // TODO: 所有错误信息的多语言化
            };
        }
    }
}

export const sdkMgmt = new SdkMgmt();